import { useEffect, useState, useCallback } from 'react';
import { Fingerprint, Lock } from 'lucide-react';
import { isNative } from '@/native/capacitorShell';
import { useAuthStore } from '@/store/authStore';
import {
  isBiometricEnabled, getBiometryInfo, verifyBiometric,
  markBackgrounded, shouldRelock, clearBackgrounded,
} from '@/native/biometricLock';

// Native-only lock screen. Locks only after a long background gap (15 min) and always
// offers a password fallback (sign out → normal login).
export default function BiometricLockGate() {
  const { isAuthenticated, logout } = useAuthStore();
  const [locked, setLocked] = useState(false);
  const [label, setLabel] = useState('Biometric unlock');
  const [error, setError] = useState('');

  const unlock = useCallback(async () => {
    setError('');
    try {
      await verifyBiometric('Unlock Numuv');
      clearBackgrounded();
      setLocked(false);
    } catch (e) {
      setError('Not recognised. Try again or use your password.');
    }
  }, []);

  useEffect(() => {
    if (!isNative || !isAuthenticated) return;
    let handle;
    (async () => {
      const info = await getBiometryInfo();
      setLabel(info.label);
      const { App } = await import('@capacitor/app');
      handle = await App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) { markBackgrounded(); return; }
        if (isBiometricEnabled() && info.available && shouldRelock()) setLocked(true);
      });
    })();
    return () => { handle?.remove?.(); };
  }, [isAuthenticated]);

  useEffect(() => { if (locked) unlock(); }, [locked, unlock]);

  if (!locked) return null;

  return (
    <div
      className="fixed inset-0 z-[300] bg-[#0a0a0b] flex flex-col items-center justify-center gap-6 px-8"
      data-testid="biometric-lock-screen"
    >
      <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
        <Lock className="w-7 h-7 text-emerald-400" />
      </div>
      <div className="text-center">
        <h2 className="text-white text-lg font-semibold">Numuv is locked</h2>
        <p className="text-zinc-500 text-sm mt-1">Unlock with {label} to continue</p>
        {error && <p className="text-red-400 text-xs mt-3" data-testid="biometric-lock-error">{error}</p>}
      </div>
      <button
        onClick={unlock}
        className="flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-sm font-medium active:scale-[0.97] transition-transform"
        data-testid="biometric-unlock-button"
      >
        <Fingerprint className="w-4 h-4" /> Unlock
      </button>
      <button
        onClick={() => { clearBackgrounded(); setLocked(false); logout(); }}
        className="text-zinc-500 text-xs underline"
        data-testid="biometric-use-password-button"
      >
        Use password instead
      </button>
    </div>
  );
}
